<template>
  <div class="gs-name-select" :class="{ 'gs-name-select--full': size === 'full', 'gs-name-select--action': size === 'action' }">
    <button ref="trigger" type="button" class="gs-name-select__trigger" :disabled="disabled"
      :aria-label="placeholder || 'Select an option'" :aria-haspopup="isActionMenu ? 'menu' : 'listbox'" :aria-expanded="String(open)"
      :aria-controls="listId" :title="selectedText" @click="toggle" @keydown.down.prevent="show" @keydown.up.prevent="show">
      <span>{{ selectedText || placeholder || 'Select an option' }}</span>
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
    </button>
    <div v-show="open" ref="popup" class="gs-name-select__popup" :style="popupStyle" @keydown="onKeydown">
      <div v-if="!isActionMenu" class="gs-name-select__search">
        <input ref="search" v-model="query" type="search" placeholder="Search options…" aria-label="Search options"
          role="combobox" aria-autocomplete="list" :aria-expanded="String(open)" :aria-controls="listId"
          :aria-activedescendant="activeIndex >= 0 ? `${listId}-${activeIndex}` : null" @input="activeIndex = 0" />
      </div>
      <div :id="listId" ref="list" class="gs-name-select__list" :role="isActionMenu ? 'menu' : 'listbox'" :aria-label="placeholder || 'Options'">
        <button v-for="(option, index) in filteredOptions" :id="`${listId}-${index}`" :key="index" type="button"
          :role="isActionMenu ? 'menuitem' : 'option'" :aria-selected="isActionMenu ? null : String(option.value === value)" :disabled="option.value === '---' || option.disabled"
          :class="{ 'is-active': index === activeIndex, 'is-selected': !isActionMenu && option.value === value }"
          @mouseenter="activeIndex = index" @click="select(option)">
          <span>{{ option.text }}</span><span v-if="!isActionMenu && option.value === value" aria-hidden="true">✓</span>
        </button>
        <p v-if="!filteredOptions.length" class="gs-name-select__empty" role="status">No matching options</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GsNameSelect',
  props: {
    value: { default: null }, options: { type: Array, default: () => [] },
    actions: { type: Array, default: () => [] }, maxHeight: { type: String, default: '400px' },
    placeholder: { type: String, default: '' }, size: { type: String, default: 'regular' }, disabled: Boolean,
  },
  data: () => ({ open: false, query: '', activeIndex: -1, popupStyle: {} }),
  computed: {
    listId() { return `gs-name-options-${this._uid}`; },
    isActionMenu() { return this.actions.length > 0; },
    normalizedOptions() { if (this.isActionMenu) return this.actions.map((action, value) => ({ ...action, value })); return this.options.map(option => option && typeof option === 'object' && 'value' in option ? option : { value: option, text: String(option) }); },
    selectedText() { if (this.isActionMenu) return ''; const option = this.normalizedOptions.find(item => item.value === this.value); return option ? String(option.showValue || option.text) : ''; },
    filteredOptions() { const query = this.query.trim().toLocaleLowerCase(); return this.normalizedOptions.filter(option => String(option.text).toLocaleLowerCase().includes(query)); },
  },
  watch: {
    disabled(value) { if (value) this.close(); },
    options() { this.activeIndex = 0; },
  },
  mounted() {
    // Escape clipping in scrollable editors and dialogs, retaining Vue ownership.
    document.body.appendChild(this.$refs.popup);
    document.addEventListener('mousedown', this.onOutside);
    document.addEventListener('focusin', this.onOutside);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll, true);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside);
    document.removeEventListener('focusin', this.onOutside);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll, true);
    if (this.$refs.popup && this.$refs.popup.parentNode) this.$refs.popup.parentNode.removeChild(this.$refs.popup);
  },
  methods: {
    toggle() { if (this.open) this.close(); else this.show(); },
    show() {
      if (this.disabled) return;
      this.query = ''; this.open = true;
      this.activeIndex = Math.max(0, this.normalizedOptions.findIndex(option => option.value === this.value));
      this.$nextTick(() => { this.position(); if (this.isActionMenu) this.$refs.list.querySelector('button:not(:disabled)')?.focus(); else this.$refs.search.focus(); this.revealActive(); });
    },
    close() { this.open = false; },
    position() {
      const rect = this.$refs.trigger.getBoundingClientRect();
      const labelWidth = Math.max(0, ...Array.from(this.$refs.list.querySelectorAll('button > span:first-child')).map(label => label.scrollWidth + 72));
      const minimum = this.isActionMenu || this.size === 'action' ? 320 : 520;
      const width = Math.min(Math.max(rect.width, minimum, labelWidth), window.innerWidth - 24);
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const up = below < 260 && above > below;
      const height = Math.min(400, Math.max(100, up ? above : below));
      this.popupStyle = { width: `${width}px`, left: `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`,
        top: up ? 'auto' : `${rect.bottom + 6}px`, bottom: up ? `${window.innerHeight - rect.top + 6}px` : 'auto', maxHeight: `min(${height}px, ${this.maxHeight})` };
    },
    onOutside(event) { if (!this.$el.contains(event.target) && !this.$refs.popup.contains(event.target)) this.close(); },
    onResize() { if (this.open) this.position(); },
    onScroll(event) { if (this.open && !this.$refs.popup.contains(event.target)) this.close(); },
    select(option) {
      if (this.disabled || option.disabled || option.value === '---') return;
      this.close(); this.$refs.trigger.focus();
      if (this.isActionMenu) { if (typeof option.callback === 'function') option.callback(); return; }
      this.$emit('input', option.value); this.$emit('change', option.value); this.$emit('changeValue', option.value);
    },
    revealActive() {
      this.$nextTick(() => { const item = document.getElementById(`${this.listId}-${this.activeIndex}`); if (item && item.scrollIntoView) item.scrollIntoView({ block: 'nearest', inline: 'nearest' }); if (this.isActionMenu && item) item.focus(); });
    },
    onKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); this.$refs.trigger.focus(); }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault(); const length = this.filteredOptions.length;
        if (length) { this.activeIndex = (this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + length) % length; this.revealActive(); }
      }
      if (event.key === 'Enter') { event.preventDefault(); const option = this.filteredOptions[this.activeIndex]; if (option) this.select(option); }
      if (event.key === 'Tab') { this.close(); this.$refs.trigger.focus(); }
    },
  },
};
</script>

<style>
.gs-name-select { width: 360px; max-width: 100%; min-width: 0; flex-shrink: 1; color: var(--gs-foreground, #172033); }
.gs-name-select--action { width: 240px; }
.gs-name-select--full { width: 100%; }
.gs-name-select__trigger { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; min-height: 42px; padding: 10px 14px; border: 1px solid var(--gs-border, #dce3ef); border-radius: 10px; background: var(--gs-surface, #fff); text-align: left; font-size: 14px; line-height: 20px; cursor: pointer; color: inherit; }
.gs-name-select__trigger > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.gs-name-select__trigger svg { flex-shrink: 0; color: var(--gs-muted-foreground, #65748b); }
.gs-name-select__trigger:focus-visible, .gs-name-select__search input:focus-visible { outline: 2px solid #2879ff; outline-offset: 2px; }
.gs-name-select__trigger[aria-expanded=true] { border-color: #2879ff; box-shadow: 0 0 0 3px #2879ff15; }
.gs-name-select__trigger:disabled { opacity: .55; cursor: not-allowed; }
.gs-name-select__popup { position: fixed; z-index: 10050; display: flex; flex-direction: column; overflow: hidden; border: 1px solid var(--gs-border, #dce3ef); border-radius: 14px; background: var(--gs-surface, #fff); color: var(--gs-foreground, #172033); box-shadow: 0 16px 48px #18233b26; font: 14px/1.5 Inter, system-ui, sans-serif; }
.gs-name-select__search { padding: 12px; border-bottom: 1px solid var(--gs-border, #e1e8f0); }
.gs-name-select__search input { width: 100%; min-width: 0; padding: 10px 12px; border: 1px solid var(--gs-border, #dce3ef); border-radius: 8px; background: var(--gs-background, #f6f8fc); color: inherit; font: inherit; }
.gs-name-select__list { padding: 6px; min-height: 0; overflow: auto; overscroll-behavior: contain; }
.gs-name-select__list button { display: flex; justify-content: space-between; gap: 16px; width: 100%; min-width: max-content; padding: 11px 12px; border: 0; border-radius: 8px; background: transparent; text-align: left; color: inherit; font: inherit; cursor: pointer; }
.gs-name-select__list button > span:first-child { white-space: nowrap; flex-shrink: 0; }
.gs-name-select__list button.is-active, .gs-name-select__list button:hover { background: #f0f6ff; }
.gs-name-select__list button.is-selected { color: #1766dc; background: #edf5ff; font-weight: 600; }
.gs-name-select__list button:disabled { opacity: .5; cursor: default; }
.gs-name-select__empty { padding: 20px 12px; color: var(--gs-muted-foreground, #65748b); }
@media (max-width: 640px) { .gs-name-select { width: 100%; } }
</style>
